import React, { useEffect, useMemo, useState } from 'react';
import { LightTable, LightTableColumDef } from '../../../theme/components';
import { getPolyglot } from '../../../i18n';
import { Link as LinkRouter } from 'react-router-dom';
import { LabelState } from '../../../theme/components';
import { useAppDispatch, useAppSelector } from '../../../redux/store.model';
import { Button, Link } from '@mui/material';
import StoerkIdAPI from '../../../redux/stoerkID/StoerkIdAPI';
import UserAPI from '../../../redux/users/api/UserAPI';
import { getUserSelector } from '../../../redux/users/api/User.selectors';
import StoerkIdAddTeamMemberModal from '../StoerkIdDetails/StoerkIdTeamMembers/StoerkIdAddTeamMemberModal';
import { WorkspaceAPI } from '../../../redux/stoerkID/StoerkIdAPI.model';

interface OrganizationTableProps {
  childIds: string[];
  parentWorkspaceId: string;
}

function OrganizationTable(props: OrganizationTableProps) {
  const { childIds, parentWorkspaceId } = props;
  const polyglot = getPolyglot();
  const dispatch = useAppDispatch();
  const [inviteTarget, setInviteTarget] = useState<WorkspaceAPI | null>(null);

  // Fetch workspace details and usage for each child
  useEffect(() => {
    childIds.forEach((childId) => {
      dispatch(StoerkIdAPI.endpoints.getWorkspace.initiate(childId));
      dispatch(StoerkIdAPI.endpoints.getWorkspaceUsage.initiate(childId));
    });
  }, [dispatch, childIds]);

  const childWorkspaces = useAppSelector((state) =>
    childIds.map((childId) => {
      const workspace =
        StoerkIdAPI.endpoints.getWorkspace.select(childId)(state).data;
      const usage =
        StoerkIdAPI.endpoints.getWorkspaceUsage.select(childId)(state).data;
      return { childId, workspace, usage };
    })
  );

  // Fetch first user of each child workspace
  useEffect(() => {
    childWorkspaces.forEach(({ workspace }) => {
      if (workspace?.users?.[0]) {
        dispatch(UserAPI.endpoints.getUser.initiate(workspace.users[0]));
      }
    });
  }, [dispatch, childWorkspaces]);

  const columns: LightTableColumDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: polyglot.t('stoerk_id.organization_list.header_name'),
        orderable: true,
      },
      {
        field: 'ownerEmail',
        headerName: polyglot.t(
          'stoerk_id.organization_list.header_owner_email'
        ),
        orderable: true,
      },
      {
        field: 'status',
        headerName: polyglot.t('stoerk_id.organization_list.header_status'),
        orderable: true,
      },
      {
        field: 'commanders',
        headerName: polyglot.t('stoerk_id.organization_list.header_commanders'),
        orderable: true,
        TableCellProps: { align: 'right' },
      },
      {
        field: 'quota',
        headerName: polyglot.t('stoerk_id.organization_list.header_quota'),
        orderable: false,
        TableCellProps: { align: 'center' },
      },
      {
        field: 'actions',
        headerName: '',
        TableCellProps: { align: 'center', width: '150px' },
      },
    ],
    [polyglot]
  );

  const rows = useMemo(
    () =>
      childWorkspaces.map(({ childId, workspace, usage }) => {
        const name = workspace?.name ?? childId;
        const firstUserId = workspace?.users?.[0];

        return {
          id: childId,
          name: {
            value: name,
            element: (
              <Link
                to={`/account/organizations/${childId}`}
                component={LinkRouter}
              >
                {name}
              </Link>
            ),
          },
          ownerEmail: firstUserId ? <OwnerEmail userId={firstUserId} /> : '',
          status: {
            value: workspace ? 'active' : 'unknown',
            element: (
              <LabelState variant={workspace ? 'success' : 'info'}>
                {workspace
                  ? polyglot.t('stoerk_id.organization_list.status_active')
                  : polyglot.t('stoerk_id.organization_list.status_unknown')}
              </LabelState>
            ),
          },
          commanders: usage?.commanders?.length ?? 0,
          quota: workspace
            ? `${usage?.users?.length ?? 0}/${
                workspace.quota.users
              } ${polyglot.t('stoerk_id.organization_list.users_short')} · ${
                usage?.commanders?.length ?? 0
              }/${workspace.quota.devices} ${polyglot.t(
                'stoerk_id.organization_list.devices_short'
              )}`
            : '-',
          actions: workspace ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setInviteTarget(workspace)}
            >
              {polyglot.t('stoerk_id.organization_list.invite_user')}
            </Button>
          ) : null,
        };
      }),
    [childWorkspaces, polyglot]
  );

  return (
    <>
      <LightTable columns={columns} rows={rows} variant="clear" />
      {inviteTarget && (
        <StoerkIdAddTeamMemberModal
          open={!!inviteTarget}
          onClose={() => setInviteTarget(null)}
          workspace={inviteTarget}
        />
      )}
    </>
  );
}

/** Small helper to display owner email from user ID */
function OwnerEmail({ userId }: { userId: string }) {
  const user = useAppSelector((state) => getUserSelector(userId)(state).data);
  return <>{user?.email ?? userId}</>;
}

export default OrganizationTable;
